// jmix-exit E3a.3: the SPA information architecture — a faithful port of the Jmix
// `src/main/resources/.../menu.xml` + `MainView.java` role behaviour. Kept as a pure data + function
// module (no React) so the structure and the role rules are unit-testable in isolation
// (App.nav.test.ts / App.routing.test.ts), mirroring backoffice's frontend/src/App.tsx NAV model.
//
// Three groups survive the port (the Jmix `opened` flag → group `mode`):
//   • application  (FOSTER portal)        — dashboard, my-animals, availability, contact, my-profile
//   • selfservice  (service-account ops)  — my-service-account
//   • security     (admin / FullAccess)   — users, service-accounts, organizations, organization-rotation
//
// Deliberately DROPPED from menu.xml (no app screen behind them — they are Jmix framework UI):
//   • security/resourceRoles, security/rowLevelRoles  → Jmix `sec_*Model` design-time role editors,
//     replaced by Spring RBAC authorities (Epic 5). Not a user feature.
//   • datatools/entityInspector, datatools/dataModel  → Jmix datatools, decommissioned in E10.
//
// Unlike backoffice (strangler-fig, legacy Jmix hand-off links), platform is BIG-BANG: there is no
// coexistence and no /new/** edge prefix — every nav item is an in-SPA route. The screens themselves
// are built in Epics 7–9; here every page renders the shared "under construction" placeholder.

/** The three SPA roles, mapped from the Jmix roles that drive menu visibility + landing. */
export type Role = "foster" | "admin" | "self-service";
export const ROLES: readonly Role[] = ["foster", "admin", "self-service"] as const;

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/** Every page the shell can render. Each maps to a Jmix `@Route` (see ROUTE_BY_PAGE). */
export type Page =
  // application group (FOSTER portal)
  | "dashboard"
  | "my-animals"
  | "availability"
  | "contact"
  | "my-profile"
  // selfservice group
  | "self-service"
  // security group (admin)
  | "users"
  | "persons"
  | "service-accounts"
  | "organizations"
  | "organization-rotation"
  // shell-only (no nav item)
  | "service-account-detail" // dedicated detail view reached from the service-accounts list
  | "animal-detail" // jmix-8.5: foster animal detail reached from Mijn dieren / Dashboard
  | "home" // admin landing (the Jmix StandardMainView "/" home — overview placeholder)
  | "under-construction"; // an unknown / not-yet-built deep link, handled in-SPA

export type GroupId = "application" | "selfservice" | "security";
/** open → expanded by default; collapsed → an accordion collapsed by default (`opened="false"`). */
export type GroupMode = "open" | "collapsed";

export interface NavItemDef {
  page: Exclude<Page, "home" | "under-construction">;
  /** i18n key — the Dutch label from the Jmix message bundle. */
  labelKey: string;
  /** Jmix `@Route` value (the deep-link path, without a leading slash). */
  route: string;
  /** Icon key resolved to a @fawkes/ui icon component in App.tsx (keeps this module React-free). */
  icon: string;
}

export interface NavGroup {
  id: GroupId;
  titleKey: string;
  mode: GroupMode;
  /** The roles that see this group (role-driven visibility — MainView's foster-hide rule generalised). */
  roles: Role[];
  /** Optional section glyph key (collapsed groups get one, mirroring the Jmix Security shield). */
  sectionIcon?: string;
  items: NavItemDef[];
}

/** The authoritative menu model — group order + item order reproduce menu.xml exactly. */
export const NAV: NavGroup[] = [
  {
    id: "application",
    titleKey: "menu.application.title",
    mode: "open", // menu.xml: opened="true"
    roles: ["foster"], // MainView hides this group from non-FOSTER users
    items: [
      { page: "dashboard", labelKey: "menu.dashboard", route: "dashboard", icon: "dashboard" },
      { page: "my-animals", labelKey: "menu.myAnimals", route: "animals", icon: "paw" },
      { page: "availability", labelKey: "menu.availability", route: "availability", icon: "calendar" },
      { page: "contact", labelKey: "menu.contact", route: "contact", icon: "envelope" },
      // "Mijn gegevens" (my-profile) is uit de foster-nav gehaald (productbesluit 2026-07-03);
      // de pagina/het Page-type blijft bestaan voor een eventuele terugkeer.
    ],
  },
  {
    id: "selfservice",
    titleKey: "menu.selfservice.title",
    mode: "open", // menu.xml: opened="true"
    roles: ["self-service"],
    items: [
      {
        page: "self-service",
        labelKey: "menu.selfservice.myServiceAccount",
        route: "service-account-self-service",
        icon: "lock",
      },
    ],
  },
  {
    id: "security",
    titleKey: "menu.security.title",
    mode: "collapsed", // menu.xml: opened="false"
    roles: ["admin"],
    sectionIcon: "shield",
    items: [
      { page: "users", labelKey: "menu.users", route: "users", icon: "users" },
      { page: "persons", labelKey: "menu.persons", route: "persons", icon: "user" },
      { page: "service-accounts", labelKey: "menu.serviceAccounts", route: "service-accounts", icon: "idcard" },
      { page: "organizations", labelKey: "menu.organizations", route: "organizations", icon: "building" },
      { page: "organization-rotation", labelKey: "menu.organizationRotation", route: "organization-rotation", icon: "rotate" },
    ],
  },
];

/** Per-page header title key (i18n). Shell-only pages (home / under-construction) included. */
export const PAGE_TITLE_KEY: Record<Page, string> = {
  dashboard: "menu.dashboard",
  "my-animals": "menu.myAnimals",
  availability: "menu.availability",
  contact: "menu.contact",
  "my-profile": "menu.myProfile",
  "self-service": "menu.selfservice.myServiceAccount",
  users: "menu.users",
  persons: "menu.persons",
  "service-accounts": "menu.serviceAccounts",
  "service-account-detail": "menu.serviceAccounts",
  "animal-detail": "menu.myAnimals",
  organizations: "menu.organizations",
  "organization-rotation": "menu.organizationRotation",
  home: "shell.home.title",
  "under-construction": "shell.underConstruction.title",
};

/** The Jmix `@Route` for each page (deep-link path, no leading slash). */
const ROUTE_BY_PAGE: Partial<Record<Page, string>> = Object.fromEntries(
  NAV.flatMap((g) => g.items).map((i) => [i.page, i.route]),
);

/**
 * Role-driven group visibility — the SPA equivalent of MainView hiding the foster-portal group from
 * non-FOSTER users (generalised to all three roles). This is UX only; the real route/data authz is
 * enforced server-side (Epic 5 RBAC + Epic 3b token authorities), never by hiding a menu entry.
 *
 * jmix review 3a-T2: ADDITIVE over the principal's roles. Jmix menu visibility was additive-by-policy
 * (e.g. a FullAccess admin who also holds self-service sees BOTH groups), so a multi-role principal
 * gets the UNION of their groups, in NAV order. A single role still returns exactly its group(s).
 */
export function visibleNav(roles: Role | readonly Role[]): NavGroup[] {
  const wanted = new Set<Role>(typeof roles === "string" ? [roles] : roles);
  return NAV.filter((group) => group.roles.some((role) => wanted.has(role)));
}

/**
 * Role-based landing parity with `MainView.beforeEnter`:
 *   • FOSTER             → /dashboard
 *   • self-service-only  → /service-account-self-service
 *   • admin (FullAccess) → / (the StandardMainView home — an overview placeholder)
 */
export function landingPageForRole(role: Role): Page {
  switch (role) {
    case "foster":
      return "dashboard";
    case "self-service":
      return "self-service";
    case "admin":
      return "home";
  }
}

const PAGE_BY_ROUTE: Record<string, Page> = Object.fromEntries(
  NAV.flatMap((g) => g.items).map((i) => [i.route, i.page]),
);

/**
 * Map an entry path to the initial page (deep-link support). Root ("" / "/") resolves to the admin
 * "home"; App.tsx overrides root with `landingPageForRole(role)` so the role landing matches Jmix.
 * An unknown / not-yet-built path resolves to the in-SPA "under-construction" placeholder (keeps the
 * operator in the shell instead of a blank screen).
 */
export function initialPageFromPath(pathname: string): Page {
  const path = pathname.replace(/\/+$/, ""); // drop trailing slashes
  if (path === "" ) return "home";
  const segment = path.replace(/^\//, "");
  return PAGE_BY_ROUTE[segment] ?? "under-construction";
}

export { ROUTE_BY_PAGE };
